import { makeObservable, observable } from 'mobx'
import { queryClient } from '../../../App'
import {
  isAdmin,
  isAdministration,
  isOperator,
} from '../../../services/AccountService'
import {
  addComment,
  assignDealer,
  editReportedRequest,
  markOrderAsDuplicate,
  markOrderAsDuplicateAdministration,
  markOrderAsImpossible,
  resetToCreated,
  updateFinancialSource,
  updateRequest,
} from '../../../services/ApiService'
import {
  notifyError,
  notifySuccess,
} from '../../../services/ToastifyService/ToastifyService'
import RequestBaseStore from '../../../stores/RequestBaseStore'
import {
  closeModalFn,
  openModalFn,
} from '../../../utils/controllers/ModalController'
import { getComp } from '../../../utils/utils/DI'
import requestsTableStore from '../components/RequestsTableOptimized/RequestsTableOptimizedStore'
import { REQUEST_CLARIFICATION_KEYS } from '../RequestsSecondOperator/Table/hooks/useRequestsData'

class RequestManagementStore extends RequestBaseStore {
  contentState = 'default'
  newDealer = ''
  newEquipment = ''
  comment = ''

  constructor({ operatorStore, ordersStore }) {
    super()
    this.dealersStore = getComp('DealersStore')
    this.statusesStore = getComp('StatusesStore')
    this.ordersStore = ordersStore
    this.operatorStore = operatorStore

    makeObservable(this, {
      contentState: observable,
      newDealer: observable,
      newEquipment: observable,
      comment: observable,
    })
  }

  setOrderDataRequest = (isSuspicious) => {
    const reloadCallback = () => {
      isOperator() &&
        queryClient.invalidateQueries({
          queryKey: [REQUEST_CLARIFICATION_KEYS.GET_REQUEST_TABLE],
        })
      if (
        (isOperator() && this.operatorStore.selectedTab === 0) ||
        (isAdministration() && this.operatorStore.selectedTab === 1)
      ) {
        this.operatorStore.updateOrder(this.data.id)
      } else {
        this.ordersStore.updateOrder(this.data.id).then(() => {
          requestsTableStore.addServiceProps(this.ordersStore.collection)
        })
      }
    }
    if (isSuspicious) {
      this.data.isSuspicious = !this.data.isSuspicious
      this.setOrderData(
        this.data.id,
        { isSuspicious: this.data.isSuspicious },
        reloadCallback,
      )
    } else {
      this.data.hasDuplicates = !this.data.hasDuplicates
      this.setOrderData(
        this.data.id,
        { hasDuplicates: this.data.hasDuplicates },
        reloadCallback,
      )
    }
  }

  setComment = (e) => {
    this.comment = e.target.value
  }

  toDefaultView = () => {
    this.comment = ''
    this.contentState = 'default'
  }

  toAssignDealerView = () => {
    this.contentState = 'assignDealer'
  }

  toChangeStatusView = () => {
    this.contentState = 'changeStatus'
  }
  toEditDataView = () => {
    this.contentState = 'editData'
  }

  clear = () => {
    this.newDealer = ''
    this.newStatus = 0
    this.photoSettings.setNewStatus = 0
    this.comment = ''
    this.newEquipment = ''
    this.toDefaultView()
    super.clear()
  }

  onNewDealerChange = (aid) => {
    this.newDealer = aid
  }

  setNewEquipment = (value) => {
    this.newEquipment = value
  }

  get availableStatuses() {
    const { status } = this.data
    const availableStatuses = [1, 2, 7, 10, 11]

    const statuses = this.statusesStore.orderStatuses.filter((el) =>
      availableStatuses.includes(el.id),
    )
    const toDuplicate = statuses.filter((el) => el.id === 10)
    const includeToDuplicate = [9, 4, 5, 6, 2].includes(status)

    if (isAdministration() && status === 11) {
      return statuses.filter((el) => el.id === 2)
    }

    if (isAdministration()) {
      return statuses.filter((el) => el.id === 10)
    }

    if (isAdmin() && (status === 2 || status === 4)) {
      return statuses.filter((el) => el.id === 10 || el.id === 1)
    }

    return includeToDuplicate
      ? toDuplicate
      : status === 7
        ? statuses.filter((el) => el.id !== 11 && el.id !== 7 && el.id !== 1)
        : status === 11
          ? statuses.filter(
              (el) =>
                (isAdmin() || el.id !== 10) && el.id !== 11 && el.id !== 1,
            )
          : statuses.filter((el) => el.id !== 7 && el.id !== 11 && el.id !== 1)
  }
  getCurrentOrder() {
    return this.ordersStore.collection.find(
      (order) => order.id === this.data.id,
    )
  }

  reloadCallback = () => {
    this.ordersStore
      .updateOrder(this.data.id)
      .then((order) => {
        if (order) {
          this.setData(order)
          this.getImages()
        } else {
          closeModalFn['request-details']()
        }
      })
      .then(() => {
        setTimeout(() => {
          requestsTableStore.addServiceProps()
        }, 400)
      })
    this.toDefaultView()
  }

  assignDealer = (callback) => {
    const endAction = () => {
      if (typeof callback === 'function') {
        callback()
      } else {
        this.reloadCallback()
      }
      notifySuccess('Дилер назначен')
    }
    assignDealer({
      dealerId:
        isOperator() || (isAdministration() && this.data.status === 11)
          ? this.data.dealerId
          : this.dealersStore.getDealer(this.newDealer || this.data.dealer)
              ?.subjectId,
      orderIds: [this.data.id],
    }).then(() => {
      if (this.comment) {
        addComment({ orderId: this.data.id, text: this.comment }).then(() => {
          endAction()
        })
      } else {
        endAction()
      }
    })
  }
  markAsImpossible = (callback) => {
    markOrderAsImpossible({
      orderId: this.data.id,
      comment: this.comment,
    }).then(() => {
      if (callback) {
        callback()
      } else {
        this.reloadCallback()
      }
      this.comment = ''
      notifySuccess('Заявка обновлена')
    })
  }

  addComment = (callback) => {
    if (!this.comment) {
      return
    }

    addComment({ orderId: this.data.id, text: this.comment })
      .then((_) => (this.comment = ''))
      .then((_) => callback && callback())
      .then(() => notifySuccess('Заявка обновлена'))
  }

  markAsDuplicate = (operatorCallback) => {
    openModalFn['progress-backdrop']()
    if (isAdministration()) {
      markOrderAsDuplicateAdministration({
        orderId: this.data.id,
        comment: this.comment,
      })
        .then(() => {
          if (operatorCallback) {
            operatorCallback()
          } else {
            this.reloadCallback()
          }
        })
        .then(() => {
          closeModalFn['progress-backdrop']()
          notifySuccess('Заявка обновлена')
        })
        .catch(() => {
          closeModalFn['progress-backdrop']()
        })
    } else {
      markOrderAsDuplicate({
        orderId: this.data.id,
      })
        .then(() => {
          if (operatorCallback) {
            operatorCallback()
          } else {
            this.reloadCallback()
          }
        })
        .then(() => {
          closeModalFn['progress-backdrop']()
          notifySuccess('Заявка обновлена')
        })
        .catch(() => {
          closeModalFn['progress-backdrop']()
        })
    }
  }

  editReported = (files, onSuccess) => {
    const filesData = files.map((file) => file.file)
    editReportedRequest(this.data.id, {
      comment: this.comment,
      ...filesData,
    })
      .then((res) => this.checkUploadedImages(res, true))
      .then((res) => {
        if (res) {
          this.comment = ''
          onSuccess && onSuccess()
          this.getImages()
        }
      })
      .catch(() => {
        closeModalFn['progress-backdrop']()
      })

    this.comment = ''
  }

  checkUploadedImages = (res, withoutUpdate) => {
    const { uploaded, message, images } = res
    const errorImages = images.filter((el) => !el.isValid)
    closeModalFn['progress-backdrop']()
    if (uploaded) {
      notifySuccess(message)
      if (!withoutUpdate) {
        this.apiCallback()
      }
      return true
    }
    if (errorImages.length) {
      errorImages.forEach((el) => {
        notifyError(el.error + ': ' + el.filename)
      })

      return false
    }
    if (!uploaded) {
      if (!errorImages.length) {
        notifyError(message)
      }

      return false
    }

    return true
  }

  resetStatusToCreated = (operatorCallback) => {
    resetToCreated({ orderId: this.data.id }).then(() => {
      if (operatorCallback) {
        operatorCallback()
      } else {
        this.reloadCallback()
      }
      notifySuccess('Заявка обновлена')
    })
  }

  changeEquipment = (callback) => {
    updateRequest(this.data.id, {
      equipmentId: this.newEquipment,
      updateComment: this.comment,
    })
      .then(() => {
        this.ordersStore.updateOrder(this.data.id).then((order) => {
          if (order) {
            this.setData(order)
          } else {
            closeModalFn['request-details']()
          }
          notifySuccess('Id оборудования обновлён')
          this.comment = ''
        })
      })
      .then(() => {
        if (callback) {
          callback()
        }
      })
  }

  changeFinancialSource = (id, financialSource) => {
    openModalFn['progress-backdrop']()

    updateFinancialSource({
      orderId: id,
      financialSourceId: financialSource,
    })
      .then(() => {
        isOperator() &&
          queryClient.invalidateQueries({
            queryKey: [REQUEST_CLARIFICATION_KEYS.GET_REQUEST_TABLE],
          })
        this.reloadCallback()
        notifySuccess('Тип оплаты изменен')
        closeModalFn['progress-backdrop']()
      })
      .catch(() => {
        closeModalFn['progress-backdrop']()
      })
  }
}

export default RequestManagementStore
